import { createSelector } from 'reselect'
import _ from 'lodash'
import { PROFILE_PICTURE_PLACEHOLDER } from '../common/constants'
import { getNominative } from '../common/utils'
import {
  getLogsList,
  getResidentsList,
  getUserHome,
  getUsersList,
  getLogCategories,
  getLogCategoryItems
} from './Lists'
import { HomeTier, Responsibility } from '@lmc/ts-common'
import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'

const getCurrentFilters = (state) => state.global.filters.logs
const getCurrentUser = (state) => state.global.user

export const getSafeguardingAccess = createSelector(
  [getCurrentUser],
  (user) => {
    return user?.permissions?.some(
      (permission) => permission === Responsibility.SafeguardingOfficer
    )
  }
)

export const populateLogs = createSelector(
  [
    getLogsList,
    getResidentsList,
    getUsersList,
    getLogCategories,
    getLogCategoryItems
  ],
  (logs, residents, users, logCategories, logCategoryItems) => {
    if (!logs || !residents || !users || !logCategories || !logCategoryItems) {
      return null
    }

    return logs.map((log) => ({
      ...log,
      loggedBy: _.find(users, { id: log?.loggedBy }),
      editedBy: _.find(users, { id: log.editedBy }),
      resident: _.find(residents, { id: log.resident }),
      category: _.find(logCategories, { id: log.category }),
      item: _.find(logCategoryItems, { id: log.item }),
      witnessedBy: _.find(users, { id: log.witnessedBy })
    }))
  }
)

/**
 * Generates property filters for the logs dashboard, and generates options for
 * these filters using list data. Options include label, value, and either
 * picture or icon + iconColor.
 */
export const getLogFilters = createSelector(
  [
    getCurrentFilters,
    getResidentsList,
    getUsersList,
    getLogCategories,
    getLogCategoryItems,
    getUserHome,
    getSafeguardingAccess
  ],
  (
    filters,
    residents,
    carers,
    categories,
    categoryItems,
    home,
    safeguardingAccess
  ) => {
    if (!residents || !carers || !categories || !categoryItems || !home)
      return null

    const sortedItems = _.sortBy(categoryItems, 'category')

    const filteredItems =
      filters && filters.category && filters.category.length
        ? sortedItems.filter(({ category }) =>
            filters.category.includes(category)
          )
        : sortedItems

    const finalFilteredItems = !safeguardingAccess
      ? filteredItems.filter((item) => item.id !== SAFEGUARDING_ITEM_ID)
      : filteredItems

    const listsMap = {
      resident: residents,
      loggedBy: carers,
      category: categories,
      item: finalFilteredItems,
      ...(home.tier === HomeTier.outstanding
        ? {
            doneBySu: [
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ]
          }
        : {})
    }

    return _.reduce(
      {
        ...INITIAL_FILTERS,
        ...(home.tier === HomeTier.outstanding
          ? {
              doneBySu: {
                label: `${getNominative(home.preferredPeopleNominative, 'Service user')} involved`,
                filters: []
              }
            }
          : {})
      },
      (result, value, key) => {
        const list = listsMap[key]
        result[key] = {
          ...value,
          label:
            value.label === 'Resident'
              ? getNominative(home.preferredPeopleNominative, 'Resident')
              : value.label,
          filters: list.map((listItem) => {
            if (key === 'doneBySu') {
              return listItem
            }
            let resultItem = {
              label: listItem.name.first
                ? `${listItem.name.first} ${listItem.name.last}`
                : listItem.name,
              value: listItem.id
            }
            if (listItem.picture || (listItem.name && listItem.name.first)) {
              resultItem = {
                ...resultItem,
                picture: listItem.picture || PROFILE_PICTURE_PLACEHOLDER
              }
            }
            if (listItem.icon || listItem.color) {
              let iconColor = listItem.color
              if (!listItem.color) {
                const category = _.find(categories, { id: listItem.category })
                if (category) iconColor = category.color
              }
              resultItem = {
                ...resultItem,
                icon: listItem.icon,
                iconColor
              }
            }
            return resultItem
          })
        }
        return result
      },
      {}
    )
  }
)

const INITIAL_FILTERS = {
  resident: {
    label: 'Resident',
    filters: []
  },
  category: {
    label: 'Category',
    filters: []
  },
  item: {
    label: 'Item',
    filters: []
  },
  loggedBy: {
    label: 'Logged by',
    filters: []
  }
}
